import { useSearchLimit } from "@/components/LoginModal/useLoginLimit";
import { getSearch, setSearch } from "@/tracker/shared/global";
import { useMemoizedFn } from "ahooks";
import { useRouter } from "next/router";
import QueryString from "qs";
import { useEffect, useMemo } from "react";
import { RelationWordsView } from "./view";

export function RelationWords({ relationList }) {
  const router = useRouter();

  const { q, sort, type, filterIds, original } = router.query;

  const { onSearchLimitCheck } = useSearchLimit();

  // 封装解析URL参数并更新搜索状态的逻辑
  const parseUrlAndUpdateState = useMemoizedFn(() => {
    const currentQuery = (q as string) || "";
    const originalParam = (original as string) || "";

    // 使用URL中的original参数作为原始搜索词，如果没有则使用当前查询词
    const originalSearchWord = originalParam || currentQuery;

    // 计算标签列表（从URL中移除原始搜索词）
    const remainingQuery = currentQuery.replace(originalSearchWord, "").trim();
    const topLabelList = remainingQuery
      ? remainingQuery.split(" ").filter(Boolean)
      : [];
    const topLabelDepth = topLabelList.length;

    // 更新搜索状态
    setSearch({
      original_search_word: originalSearchWord,
      top_label_list: topLabelList,
      top_label_depth: topLabelDepth,
    });
  });

  const getNextQuery = useMemoizedFn((word: string) => {
    return {
      q: `${q} ${word}`,
      original: original || q,
      ...(sort ? { sort } : null),
      ...(type ? { type } : null),
      ...(filterIds ? { filter_ids: filterIds } : null),
    };
  });

  const onClickKeyword = useMemoizedFn(async (evt, keyWord) => {
    evt.preventDefault();

    await onSearchLimitCheck();

    // 获取当前的搜索数据
    const currentSearch = getSearch();

    // 获取原始搜索词，如果没有则使用当前搜索词
    const originalSearchWord = currentSearch.original_search_word || q;

    // 获取当前的标签列表，如果没有则初始化为空数组
    const currentLabelList = currentSearch.top_label_list || [];

    // 添加新的标签到列表中
    const newLabelList = [...currentLabelList, keyWord.word];

    // 计算标签深度
    const labelDepth = newLabelList.length;

    setSearch({
      search_word_type: "搜索推荐词",
      original_search_word: originalSearchWord,
      top_label_list: newLabelList,
      top_label_depth: labelDepth,
    });

    const query = getNextQuery(keyWord?.word);
    router.push(
      {
        pathname: "/search",
        query,
      },
      undefined,
      {
        // 如果当前页面pathname没变化则不需要执行服务端方法
        shallow: router.pathname === "/search",
      }
    );
  });

  // 监听浏览器前进后退事件
  useEffect(() => {
    const handlePopState = (event) => {
      parseUrlAndUpdateState();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [parseUrlAndUpdateState]);

  // 初始化时同步状态
  useEffect(() => {
    parseUrlAndUpdateState();
  }, [q, parseUrlAndUpdateState]);

  const list = useMemo(() => {
    if (!relationList?.length) {
      return [];
    }

    return relationList?.map((item) => ({
      ...item,
      href: `/search?${QueryString.stringify(getNextQuery(item.word))}`,
    }));
  }, [getNextQuery, relationList]);

  return (
    <RelationWordsView relationList={list} onClickKeyword={onClickKeyword} />
  );
}
